import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { organizationsService } from '../services/organizations.service';

interface FormData {
  email: string;
  name: string;
  companyName?: string;
}

interface SentEntry {
  email: string;
  name: string;
  companyName?: string;
  sentAt: string;
}

const DemoEmailPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [sentList, setSentList] = useState<SentEntry[]>([]);

  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormData>();

  const onSubmit = async (data: FormData) => {
    try {
      setLoading(true);
      const result = await organizationsService.sendDemoEmail(data);
      if (result.success) {
        toast.success(`Demo maili gönderildi → ${data.email}`);
        setSentList(prev => [
          { ...data, sentAt: new Date().toLocaleTimeString('tr-TR') },
          ...prev,
        ]);
        reset();
      } else {
        toast.error('Mail gönderilemedi, mail servisi aktif mi kontrol et.');
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Demo Maili Gönder</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Demo talep eden kişiye sistemi tanıtan otomatik mail gönderir.
        </p>
      </div>

      {/* Form */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6 mb-6">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              İsim Soyisim *
            </label>
            <input
              type="text"
              {...register('name', { required: 'İsim zorunlu' })}
              placeholder="Ahmet Yılmaz"
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              E-posta Adresi *
            </label>
            <input
              type="email"
              {...register('email', {
                required: 'E-posta zorunlu',
                pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Geçersiz e-posta' },
              })}
              placeholder="ahmet@firma.com"
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
            {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Şirket Adı <span className="text-gray-400">(opsiyonel)</span>
            </label>
            <input
              type="text"
              {...register('companyName')}
              placeholder="Yılmaz Turizm A.Ş."
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold rounded-lg transition-colors text-sm"
          >
            {loading ? 'Gönderiliyor...' : '📨 Demo Mailini Gönder'}
          </button>
        </form>
      </div>

      {/* Sent history */}
      {sentList.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
            Bu oturumda gönderilenler
          </h2>
          <div className="space-y-2">
            {sentList.map((entry, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{entry.name}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{entry.email}{entry.companyName ? ` · ${entry.companyName}` : ''}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400">{entry.sentAt}</span>
                  <span className="inline-block w-2 h-2 rounded-full bg-green-500"></span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DemoEmailPage;
